<template>
  <div class="job-info" v-if="props.job">
    <div class="header">
      <div class="text-heading" v-if="props.job.external_role_name">{{ props.job.external_role_name }}</div>
      <div v-if="props.job.closed_at" class="closed-tag">Job closed</div>
      <b-button variant="outline-primary" v-b-modal="'edit-job-modal'" v-else>Edit job details</b-button>
    </div>
    <div class="info" v-if="props.job.properties.pay_amount">
      <Pound :color="PaletteColors['default-lighten-30']" width="20" height="20"></Pound>
      {{ props.job.properties.pay_amount }}
      {{ props.job.properties.pay_frequency }}
    </div>
    <div class="info" v-if="currentLocation">
      <MapPin :color="PaletteColors['default-lighten-30']" width="20" height="20"></MapPin>
      {{ currentLocation.name }}, {{ currentLocation.properties.postcode }}
    </div>
    <b-button @click="showFullDescription = !showFullDescription" class="info see-more">
      <MinusCircle v-if="showFullDescription" :color="PaletteColors['brand-primary']" width="20" height="20" />
      <PlusCircle v-else :color="PaletteColors['brand-primary']" width="20" height="20" />
      {{ showFullDescription ? 'Hide description' : 'Show description' }}
    </b-button>
    <div v-if="props.job.properties.description && showFullDescription" class="description">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-if="props.job.properties.description_html" v-html="props.job.properties.description_html"></div>
      <div class="info" v-else>{{ props.job.properties.description }}</div>
    </div>
    <AddJobModal
      :existing-display-name="props.job.external_role_name"
      :existing-job-id="props.job.unfilled_shift_group_id"
      :existing-role-id="props.job.internal_job_role_id"
      :existing-location-id="props.job.location_id"
      :existing-pay-frequency="props.job.properties.pay_frequency"
      :existing-pay-amount="props.job.properties.pay_amount"
      :existing-description="props.job.properties?.description_html || props.job.properties.description"
      :edit-mode="true"
    />
  </div>
</template>

<script setup>
import MapPin from '@/assets/icons/MapPin.vue';
import Pound from '@/assets/icons/Pound.vue';
import { computed, ref } from 'vue';
import useTalentPoolLocations from '@/state/composables/useTalentPoolLocations';
import AddJobModal from '@/pages/talent-pool/jobs/components/AddJobModal.vue';
import { PaletteColors } from '@/Theme';
import PlusCircle from '@/assets/icons/PlusCircle.vue';
import MinusCircle from '@/assets/icons/MinusCircle.vue';

const props = defineProps({
  job: Object,
});

const { locations } = useTalentPoolLocations();

const showFullDescription = ref(false);

const currentLocation = computed(() => {
  if (locations.value.length > 0) {
    return locations.value.find(location => location.location_id === props.job.location_id);
  }
  return null;
});
</script>

<style scoped lang="scss">
.job-info {
  display: flex;
  flex-direction: column;
  background-color: var(--palette-color-base-white);
  padding: 1rem 1rem 1rem 1.5rem;
  border-radius: 0.5rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.text-heading {
  font-size: 22px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.01em;
  text-align: left;
  display: flex;
  align-items: center;
}

.closed-tag {
  color: var(--palette-color-brand-primary);
  border: 1px solid var(--palette-color-brand-primary);
  border-radius: 100px;
  padding: 0.25rem 0.5rem;
  font-size: 18px;
  font-weight: 500;
  margin: 0 1rem;
}

.info {
  display: flex;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-align: left;
  margin: 0.25rem 0;
  gap: 0.5rem;
  align-items: center;
  white-space: pre-wrap;
}

.description {
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.01em;
  text-align: left;
  margin-top: 1rem;
  white-space: pre-line;
  overflow-y: auto;
  max-height: 15rem;
  border-radius: 0.5rem;
  padding: 0.75rem;
  background-color: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-90);
  box-shadow: var(--depth-2);
}

.info.see-more {
  color: var(--palette-color-brand-primary);
  background-color: transparent;
  border: 0;
  padding: 0;
  display: flex;
  width: fit-content;

  &:hover {
    font-weight: 600;
    background-color: transparent;
    color: var(--palette-color-brand-primary);
  }

  &:focus,
  &:active {
    background-color: transparent !important;
    color: var(--palette-color-brand-primary) !important;
    box-shadow: 0 0 0 0 !important;
  }
}
</style>
